import React, { useState, useEffect } from 'react';
import DashboardModal from '@/components/dashboard-modal';
import RequestAppForm from './RequestAppForm';
import { Button } from '@/components/ui/button';
import { X, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface RequestAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: RequestAppFormData) => void;
  selectedCategory?: string;
}

export interface RequestAppFormData {
  problem: string;
  category?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'occasionally';
  currentSolution?: string;
  priority?: 'critical' | 'important' | 'nice-to-have';
  sharing?: 'solo' | 'team' | 'both';
  platform?: 'desktop' | 'mobile' | 'both';
  additionalDetails?: string;
}

const getInitialFormData = (selectedCategory?: string): RequestAppFormData => ({
  problem: '',
  category: selectedCategory || '',
  frequency: undefined,
  currentSolution: '',
  priority: undefined,
  sharing: undefined,
  platform: undefined,
  additionalDetails: ''
});

const RequestAppModal: React.FC<RequestAppModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  selectedCategory
}) => {
  const [formData, setFormData] = useState<RequestAppFormData>(() => getInitialFormData(selectedCategory));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Reset form when modal opens or selectedCategory changes
  useEffect(() => {
    if (isOpen) {
      console.log('[RequestAppModal] Modal opened, resetting form state');
      setFormData(getInitialFormData(selectedCategory));
      setIsSubmitting(false);
      setHasUnsavedChanges(false);
    }
  }, [isOpen, selectedCategory]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      console.log('[RequestAppModal] Modal closed, resetting form state');
      setFormData(getInitialFormData());
      setIsSubmitting(false);
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  const handleFormChange = (field: keyof RequestAppFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async () => {
    console.log('[RequestAppModal] Form submitted, starting processing');
    setIsSubmitting(true);
    setHasUnsavedChanges(false); // Clear unsaved changes flag since we're submitting
    onSubmit(formData);
    // Note: Modal will be closed by parent, and useEffect will handle cleanup
  };

  const handleClose = () => {
    console.log('[RequestAppModal] Manual close triggered');
    setHasUnsavedChanges(false);
    onClose();
    // Note: useEffect will handle form reset when isOpen becomes false
  };

  const isFormValid = formData.problem.length >= 50;

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Request an App"
      size="lg"
      accentColor="purple"
      hasUnsavedChanges={hasUnsavedChanges}
    >
      <div className="px-2 md:px-4">
        <RequestAppForm
          formData={formData}
          onChange={handleFormChange}
        />
        
        {/* Inline Footer Buttons */}
        <div className="flex items-center justify-between gap-4 pt-6">
          {/* Left side - Character count */}
          <div className="flex items-center gap-3">
            <div className={cn(
              "text-xs font-mono transition-colors duration-200",
              formData.problem.length < 50 ? "text-amber-600 dark:text-amber-400" :
              "text-green-600 dark:text-green-400"
            )}>
              {formData.problem.length}
            </div>
            
            {formData.problem.length >= 50 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                Ready to submit
              </motion.div>
            )}
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
              className="border-border/50 hover:border-border"
            >
              <X size={16} className="mr-2" />
              Cancel
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              className={cn(
                "bg-purple-600 hover:bg-purple-700 text-white border-purple-600",
                "disabled:bg-muted disabled:text-muted-foreground disabled:border-muted",
                "transition-all duration-200"
              )}
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"
                  />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Submit
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </DashboardModal>
  );
};

export default RequestAppModal;
